
import { Component, Mixins, Prop, Vue, Watch } from 'vue-property-decorator';
import { IVideoPlayer } from '~/components/Player/VideoPlayer.vue';
import FullScreenMediaDialog from '~/components/FullScreenMediaDialog.vue';
import VideoPlayer from '~/components/Player/VideoPlayer.vue';
import ThumbnailButton from '~/components/UIComponents/ThumbnailButton.vue';
import IconPlay from '~/components/UIComponents/IconPlay.vue';
import {
  ISanityDocuments,
  SanityDocumentSchema,
} from '~/types/sanity-documents';
import {
  IProjectState,
  RZR_EPISODE_1_UA_CUTOFF_SECS,
  RZR_EPISODE_1_SLUG,
} from '~/store/projects/types';
import { Action, State } from 'vuex-class';
import ThemeProvider from '~/components/ThemeProvider.vue';
import { ThemeName } from '~/store/types';
import AuthActions from '~/mixins/AuthActions';
import { VideoWatchEventTypes } from '~/types/event-restriction';
import { IEpisode } from '~/types/series';
import ResponsiveDialog from '~/components/ResponsiveDialog.vue';
import NextEpisodeAutoplay from './NextEpisodeAutoplay.vue';
import { SanityImageObject } from '@sanity/image-url/lib/types/types';

@Component({
  components: {
    FullScreenMediaDialog,
    VideoPlayer,
    ThumbnailButton,
    IconPlay,
    ThemeProvider,
    ResponsiveDialog,
    NextEpisodeAutoplay,
  },
  inheritAttrs: false,
})
export default class WatchEpisodeDialog extends Mixins(AuthActions) {
  @Prop({ type: Boolean, default: true })
  readonly autoplay!: boolean;
  @Prop({ type: Boolean, default: true })
  readonly closeOnEnd!: boolean;
  @Prop({ type: Number, default: 16 / 9 })
  readonly thumbAspectRatio!: number;
  @Prop({ type: Number, default: 284 })
  readonly thumbWidth!: number;
  @Prop({ type: Boolean, default: true })
  readonly enableActivatorBtnRipple!: number;
  @Prop(String) readonly id!: string;
  @Prop(String) readonly projectSlug!: string;
  @Prop(Object) readonly episode!: IEpisode;
  @Prop(Object) readonly nextEpisode!: IEpisode;
  @Prop(String) readonly activatorType!: 'text' | 'thumbnail';
  @Prop(String) readonly playLogoType!: 'cta';
  @Prop({ type: Number, default: 80 })
  readonly playIconSize!: number;
  @Prop(String) readonly activatorLabel!: string;
  @Prop({ type: Boolean, default: false })
  readonly show!: boolean;
  @Prop({ type: Boolean, default: false })
  @State((profile) => profile.user.id, { namespace: 'profile' })
  userId!: string;

  @Action('closeVideo', { namespace: 'projects' })
  private closeVideo!: () => void;

  @Action('showEpisode', { namespace: 'projects' })
  private showEpisode!: (payload: { episode: IEpisode }) => void;

  @Action('updateProjectsAutoPlayRedirect', { namespace: 'projects' })
  private updateProjectsAutoPlayRedirect!: (payload: {
    fromProjectSlug: string;
    toProjectSlug: string;
  }) => void;

  @Action('showRewardSnackbar', { namespace: 'rewards' })
  private showRewardSnackbar!: (show: boolean) => void;

  @State((state: IProjectState) => state.continueRzrEp1AfterUA, {
    namespace: 'projects',
  })
  readonly continueRzrEp1AfterUA!: IProjectState['continueRzrEp1AfterUA'];

  schemas = SanityDocumentSchema;
  isOpen = false;

  videoPlayer: IVideoPlayer | undefined = undefined;

  uaVideoCutoffSecs = RZR_EPISODE_1_UA_CUTOFF_SECS;

  showUALoginModal = false;
  showContinueNextEpisode = false;

  waitingForNextEpisode = false;

  themes = ThemeName;

  socialOptions = [
    {
      icon: 'https://static.gala.games/images/logos/social/google_icon.png',
      name: 'Google',
      connection: 'google-oauth2',
    },
    {
      icon: 'https://static.gala.games/images/logos/social/facebook_icon_royal_blue.png',
      name: 'Facebook',
      connection: 'facebook',
    },
    {
      icon: 'https://static.gala.games/images/logos/social/apple_icon.png',
      name: 'Apple',
      connection: 'apple',
    },
  ];

  @Watch('videoDoc.data.video.asset._ref', { immediate: true })
  handleAssetRefChange(
    current: string | undefined,
    previous: string | undefined,
  ) {
    if (current && current !== previous) {
      this.$sanityLookup.fetchDocument({
        _id: current,
        _type: this.schemas.MUX_ASSET,
      });
    }
  }

  @Watch('isOpen', { immediate: true })
  handleChange(currentState: boolean) {
    if (!currentState) {
      this.$emit('closed');
      this.showContinueNextEpisode = false;
      this.waitingForNextEpisode = false;
    }

    if (!this.videoPlayer) {
      return;
    }

    if (currentState) {
      this.autoplay && this.videoPlayer.play();
    } else {
      this.videoPlayer.pause();
    }
  }

  @Watch('show', { immediate: true })
  handleShowChange(currentState: boolean) {
    this.isOpen = currentState;
  }

  @Watch('episode', { immediate: true })
  handleEpisodeChange(currentState: IEpisode) {
    if (currentState && this.waitingForNextEpisode) {
      this.waitingForNextEpisode = false;
    }
  }

  async created() {
    await this.fetch();
  }

  async fetch() {
    if (this.id || this.slug) {
      this.$sanityLookup.fetchDocument({
        _id: this.id,
        slug: this.slug,
        _type: this.schemas.VIDEO,
      });
    }
  }

  get title() {
    if (!this.episode) return;

    if (this.episode.subtitle) {
      return this.episode.title
        ? `${this.episode.subtitle}: ${this.episode.title}`
        : this.episode.subtitle;
    } else if (this.episode.title) {
      return this.episode.title;
    }
  }

  get uaExperience() {
    return !this.userId && this.episode?.slug === RZR_EPISODE_1_SLUG;
  }

  get uaRedirectLogin() {
    return `${this.$route.fullPath}?tab=watch&launchrzrepisode1=1&watchmode=ua`;
  }

  get startTimeSeconds() {
    return this.continueRzrEp1AfterUA ? RZR_EPISODE_1_UA_CUTOFF_SECS : -1;
  }

  get slug() {
    return this.episode?.videoSlug;
  }

  closeUALoginModal() {
    this.$ua.trackPress({
      interactionTargetId: 'cancel',
      context: 'rzr-ep-ua-modal',
    });

    this.showUALoginModal = false;
    this.isOpen = false;
    if (
      this.videoPlayer?.getVideoCurrentTime() &&
      this.videoPlayer?.getVideoCurrentTime() >= RZR_EPISODE_1_UA_CUTOFF_SECS
    ) {
      this.videoPlayer.setCurrentTime(0);
    }
  }

  handleUASocialSignup(connection: string, name: string) {
    this.$ua.trackPress({
      interactionTargetId: `signup-${name}`,
      context: 'rzr-ep-ua-modal',
    });

    this.promptToRegister({
      redirectAfterLoginPath: this.uaRedirectLogin,
      connection,
    });
    this.isOpen = false;
  }

  handleUASignup() {
    this.$ua.trackPress({
      interactionTargetId: 'signup-gala',
      context: 'rzr-ep-ua-modal',
    });
    this.promptToRegister({ redirectAfterLoginPath: this.uaRedirectLogin });
    this.isOpen = false;
  }

  handleUALogin() {
    this.$ua.trackPress({
      interactionTargetId: 'login',
      context: 'rzr-ep-ua-modal',
    });
    this.promptToLogin({ redirectAfterLoginPath: this.uaRedirectLogin });
    this.isOpen = false;
  }

  get muxAsset() {
    if (this.videoDoc?.data?.video?.asset?._ref) {
      return (
        this.$sanityLookup.getDocumentFromId<
          ISanityDocuments[SanityDocumentSchema.MUX_ASSET]
        >({
          _id: this.videoDoc.data.video.asset._ref,
          _type: this.schemas.MUX_ASSET,
        }) || {
          isLoading: false,
          hasError: false,
        }
      );
    } else {
      return {
        isLoading: false,
        hasError: false,
      };
    }
  }

  get videoDoc() {
    const videoDoc = this.$sanityLookup.getDocumentFromIdOrSlug<
      ISanityDocuments[SanityDocumentSchema.VIDEO]
    >({
      _type: this.schemas.VIDEO,
      _id: this.id,
      slug: this.slug,
    }) || {
      isLoading: false,
      hasError: false,
    };
    return videoDoc;
  }

  triggerUALoginModal() {
    if (!this.showUALoginModal) {
      this.showUALoginModal = true;

      this.$ua.trackVideoWatchEvent({
        videoWatchEventType: VideoWatchEventTypes.VIDEO_WATCH_UA_TRIGGERED,
        videoSlug: this.slug,
      });
    }
  }

  handlePlayerMounted() {
    if (this.$refs.video as IVideoPlayer) {
      this.videoPlayer = this.$refs.video as IVideoPlayer;
    }
  }

  handleCanPlay() {
    if (this.isOpen && this.videoPlayer && this.autoplay) {
      this.videoPlayer.play();
    }
  }

  handleEnded() {
    if (this.isOpen && this.closeOnEnd) {
      this.isOpen = false;
    }
  }

  handleVideoCompleted() {
    if (this.nextEpisode) {
      this.showContinueNextEpisode = true;
    }
  }

  playNextEpisode() {
    this.$ua.trackVideoWatchEvent({
      videoWatchEventType:
        VideoWatchEventTypes.VIDEO_WATCH_NEXT_EPISODE_TRIGGERED,
      videoSlug: this.slug,
    });

    this.showContinueNextEpisode = false;
    this.showRewardSnackbar(false);

    if (this.episode.projectSlug === this.nextEpisode.projectSlug) {
      this.waitingForNextEpisode = true;
      this.showEpisode({
        episode: this.nextEpisode,
      });
    } else {
      // wanting to show first episode of a different project -> use url redirect
      this.updateProjectsAutoPlayRedirect({
        fromProjectSlug: this.episode.projectSlug,
        toProjectSlug: this.nextEpisode.projectSlug,
      });
      this.redirectToNextProject(this.nextEpisode.projectSlug, true);
    }
  }

  handleMoreDetailsClicked() {
    this.showContinueNextEpisode = false;
    this.showRewardSnackbar(false);
    if (this.episode.projectSlug !== this.nextEpisode.projectSlug) {
      // wanting to show details of a different project -> use url redirect
      this.redirectToNextProject(this.nextEpisode.projectSlug, false);
    }

    this.isOpen = false;
  }

  redirectToNextProject(projectSlug: string, startFirstEpisode: boolean) {
    const projectUrl = `/films/${projectSlug}?tab=watch`;
    const redirectUrl = startFirstEpisode
      ? projectUrl + '&playvideo=1'
      : projectUrl;
    this.$router.push(redirectUrl);
  }

  get label() {
    return this.activatorLabel || this.title
      ? this.$t('components.sanity.watchVideoDialog.activatorLabelWithTitle', {
          title: this.title,
        })
      : this.$t('components.sanity.watchVideoDialog.activatorLabel');
  }

  get thumbHeight() {
    return this.thumbAspectRatio
      ? Math.round(this.thumbWidth * (1 / this.thumbAspectRatio))
      : 0;
  }

  get key() {
    //return `${this.videoDoc?.data?._id}.${this.muxAsset?.data?.playbackId}`;
    return this.id ?? this.slug;
  }

  get thumbUrl() {
    if (!this.videoDoc?.data?.poster && !this.muxAsset?.data?.playbackId) {
      return '';
    }

    if (this.videoDoc?.data?.poster) {
      return this.$sanityImage
        .urlFor(this.videoDoc.data.poster)
        .width(this.thumbWidth)
        .height(this.thumbHeight)
        .fit('crop')
        .url();
    }

    if (this.muxAsset?.data?.playbackId) {
      const queryParams = [
        `width=${this.thumbWidth}`,
        `height=${this.thumbHeight}`,
      ];
      if (this.muxAsset?.data?.thumbTime) {
        queryParams.push(`time=${this.muxAsset.data.thumbTime}`);
      }
      return `https://image.mux.com/${
        this.muxAsset.data.playbackId
      }/thumbnail.jpg?${queryParams.join('&')}`;
    }
  }
}
